import { useEffect, useMemo, useState } from 'react';
import styles from './AdminImpersonationPanel.module.css';
import { adminService } from '../services/adminService';
import { debounce } from '../utils/debounce';

interface ImpersonationTarget {
  name?: string | null;
  email?: string | null;
  role?: string | null;
}

interface AdminStatus {
  isImpersonating: boolean;
  targetUser: ImpersonationTarget | null;
  armed: boolean;
}

interface SearchResultUser {
  id: string;
  name?: string | null;
  email?: string | null;
  role?: string | null;
}

export default function AdminImpersonationPanel({ onClose }: { onClose: () => void }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResultUser[]>([]);
  const [status, setStatus] = useState<AdminStatus>({ isImpersonating: false, targetUser: null, armed: false });
  const [loading, setLoading] = useState(false);

  const refreshStatus = async () => {
    try {
      const s = await adminService.getStatus();
      if (s) setStatus(s);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    refreshStatus();
  }, []);

  const doSearch = async (q: string) => {
    setLoading(true);
    try {
      const res = await adminService.searchUsers(q);
      setResults(res.users || []);
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = useMemo(() => debounce(doSearch, 300), []);

  useEffect(() => {
    debouncedSearch(query);
  }, [query, debouncedSearch]);

  const handleImpersonate = async (userId: string) => {
    await adminService.startImpersonation(userId);
    await refreshStatus();
  };

  const handleStop = async () => {
    await adminService.stopImpersonation();
    await refreshStatus();
  };

  const toggleArm = async () => {
    if (status.armed) {
      await adminService.disarmWrite();
    } else {
      await adminService.armWrite();
    }
    await refreshStatus();
  };

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div>Admin: Impersonation</div>
        <button className={styles.btn} onClick={onClose}>Schließen</button>
      </div>

      {status.isImpersonating && (
        <div className={styles.armed}>
          <div><strong>Aktiv:</strong> {status.targetUser?.name || status.targetUser?.email} ({status.targetUser?.role})</div>
        </div>
      )}

      {status.isImpersonating && (
        <div className={styles.warning}>
          Schreibaktionen sind blockiert, bis du "Scharf stellen" aktivierst.
        </div>
      )}

      <div className={styles.actions}>
        <button className={`${styles.btn} ${status.armed ? styles.btnDanger : styles.btnPrimary}`} onClick={toggleArm}>
          {status.armed ? 'Entschärfen' : 'Scharf stellen'}
        </button>
        {status.isImpersonating && (
          <button className={`${styles.btn}`} onClick={handleStop}>Impersonation beenden</button>
        )}
      </div>

      <div className={styles.search}>
        <input
          type="text"
          placeholder="Nutzer suchen (Name/Email)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ flex: 1, padding: '8px' }}
        />
      </div>

      <div className={styles.list}>
        {loading && <div style={{ padding: 8 }}>Suche...</div>}
        {!loading && results.map((u) => (
          <div className={styles.row} key={u.id}>
            <div>
              <div>{u.name || u.email}</div>
              <div style={{ fontSize: 12, color: '#888' }}>{u.email}</div>
            </div>
            <div className={styles.badge}>{u.role}</div>
            <button className={styles.btn} onClick={() => handleImpersonate(u.id)}>Als Nutzer verwenden</button>
          </div>
        ))}
      </div>
    </div>
  );
}
